/**
 * Giving money back.
 *
 * WHY A REASON IS REQUIRED
 * ------------------------
 * A refund is money leaving the drawer with nothing sold, so it is the single
 * easiest way to take from a till without it showing up as a discrepancy. The
 * reason is what makes a pattern visible later — twelve "wrong order" refunds
 * on one cashier's shifts is a conversation; twelve blank ones are nothing at
 * all. The backend records it against the user at critical severity.
 *
 * WHY THE AMOUNT DEFAULTS TO THE WHOLE BILL
 * -----------------------------------------
 * Most refunds are whole. Defaulting to it saves the common case a typo, and
 * a partial refund is a deliberate act that should take a deliberate edit.
 */
import { useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';

/** The reasons that actually come up, so most refunds need no typing. */
const REASONS = [
  'Wrong order',
  'Food quality',
  'Too slow',
  'Customer changed mind',
  'Billing error',
];

export function RefundPanel({ total, invoiceNumber, onRefund, onCancel, busy, error }) {
  const [amount, setAmount] = useState(String(total));
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  // Two different things, and the reports treat them differently: the sale is
  // undone (money back, food not sold), or a wrong payment goes back so the
  // same bill can be paid properly (the sale stands).
  const [kind, setKind] = useState('returned');

  const value = Number(amount) || 0;
  const tooMuch = value > total;
  const valid = value > 0 && !tooMuch && reason;

  return (
    <div>
      <h2 className="font-semibold text-lg text-ink">Refund {invoiceNumber}</h2>
      <p className="mt-0.5 text-sm text-ink-2">
        Bill total {formatPKR(total)}. This is recorded against your name.
      </p>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-sm font-medium text-ink-2">Amount to refund</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          max={total}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="h-14 w-full rounded-lg border border-rule bg-surface-3 px-3.5 text-ink text-xl font-semibold tabular-nums focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
      </label>

      {tooMuch && (
        <p role="alert" className="mt-1.5 text-sm text-bad">
          More than the bill. The most you can refund is {formatPKR(total)}.
        </p>
      )}

      <fieldset className="mt-4">
        <legend className="mb-1.5 text-sm font-medium text-ink-2">Why?</legend>
        <div className="flex flex-wrap gap-1.5">
          {REASONS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={reason === option}
              onClick={() => setReason(option)}
              className={`h-11 rounded-lg px-3.5 text-sm font-medium transition-colors ${
                reason === option
                  ? 'bg-brand-400 text-surface-2'
                  : 'bg-surface-3 text-ink-2 hover:bg-rule-strong'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="mt-3 block">
        <span className="mb-1.5 block text-sm font-medium text-ink-2">
          Anything else worth recording (optional)
        </span>
        <input
          type="text"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3.5 text-ink text-[15px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
      </label>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {error.message}
        </p>
      )}

      <fieldset className="mt-4">
        <legend className="mb-1.5 block text-sm font-medium text-ink-2">What is this refund?</legend>
        <div className="grid grid-cols-2 gap-2">
          {[
            ['returned', 'Sale returned', 'Money back, food not sold'],
            ['wrong_payment', 'Wrong payment', 'The bill will be paid again'],
          ].map(([key, label, sub]) => (
            <button
              key={key}
              type="button"
              onClick={() => setKind(key)}
              aria-pressed={kind === key}
              className={`rounded-lg border px-3 py-2 text-left ${
                kind === key ? 'border-brand-400 bg-brand-500/15 text-ink' : 'border-rule text-ink-2 hover:border-brand-400'
              }`}
            >
              <span className="block text-sm font-medium">{label}</span>
              <span className="block text-xs text-ink-3">{sub}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mt-5 flex gap-2">
        <Button variant="secondary" size="lg" onClick={onCancel} disabled={busy}>
          Back
        </Button>
        <Button
          variant="danger"
          size="lg"
          className="flex-1"
          busy={busy}
          // A refund with no reason is exactly the one nobody can account for
          // later, so it cannot be sent at all.
          disabled={!valid}
          onClick={() =>
            onRefund({
              amount: value,
              reason: detail.trim() ? `${reason} — ${detail.trim()}` : reason,
              refundMethod: 'cash',
              kind,
            })
          }
        >
          Refund {formatPKR(value)}
        </Button>
      </div>
    </div>
  );
}
